import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HealthBadges } from '@/components/HealthBadges';
import {
  Server, Shield, ShieldAlert, AlertTriangle, HardDrive, MemoryStick,
  Search, RefreshCw, ChevronRight, Activity, CheckCircle2, XCircle, Clock, Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import { MailTroubleshooterWidget } from '@/components/MailTroubleshooterWidget';
import { ConfigHistoryWidget } from '@/components/ConfigHistoryWidget';

const formatGermanDateTime = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return 'Ungültiges Datum';
    
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Fehler bei Datum';
  }
};

export default function DashboardPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterHealth, setFilterHealth] = useState('all');

  const fetchData = async () => {
    try {
      const api = authAxios();
      const [srvRes, statsRes] = await Promise.all([
        api.get('/servers'),
        api.get('/stats')
      ]);
      setServers(srvRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleImport = async () => {
    setImporting(true);
    try {
      const api = authAxios();
      const res = await api.post('/import');
      toast.success(`Import complete: ${res.data.imported} servers`);
      await fetchData();
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCloudSync = async () => {
    setSyncing(true);
    try {
      const api = authAxios();
      const res = await api.post('/cloud/sync');
      const stats = res.data.stats;
      toast.success(`Cloud Sync: +${stats.added}, Updated: ${stats.updated}, Errors: ${stats.errors}`);
      await fetchData();
    } catch (err) {
      toast.error('Cloud Sync failed. Check API credentials.');
    } finally {
      setSyncing(false);
    }
  };

  // Collect unique providers and roles
  const providers = useMemo(() => [...new Set(servers.map(s => s.provider).filter(Boolean))], [servers]);
  const roles = useMemo(() => {
    const r = new Set();
    servers.forEach(s => (s.roles_guess || []).forEach(role => r.add(role)));
    return [...r].sort();
  }, [servers]);

  // Filter and search
  const filtered = useMemo(() => {
    return servers.filter(s => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        (s.meta?.alias || '').toLowerCase().includes(q) ||
        (s.primary_fqdn || '').toLowerCase().includes(q) ||
        (s.host || '').toLowerCase().includes(q) ||
        (s.provider || '').toLowerCase().includes(q);
      const matchProvider = filterProvider === 'all' || s.provider === filterProvider;
      const matchRole = filterRole === 'all' || (s.roles_guess || []).includes(filterRole);
      const matchHealth = filterHealth === 'all' ||
        (filterHealth === 'healthy' && (s.health?.issues_count || 0) === 0) ||
        (filterHealth === 'critical' && (s.health?.issues_count || 0) > 0) ||
        (filterHealth === 'cert_expired' && s.health?.cert_expired) ||
        (filterHealth === 'cert_expiring' && s.health?.cert_expiring_soon) ||
        (filterHealth === 'failed_units' && s.health?.failed_units) ||
        (filterHealth === 'high_memory' && s.health?.high_memory) ||
        (filterHealth === 'high_disk' && s.health?.high_disk);
      return matchSearch && matchProvider && matchRole && matchHealth;
    });
  }, [servers, search, filterProvider, filterRole, filterHealth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="dashboard-loading">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
            Dashboard
          </h1>
          <p className="text-sm text-[#a1a1aa] mt-1">
            {stats?.total_servers || 0} servers monitored
            {stats?.last_import && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs text-[#52525b]">
                <Clock className="w-3 h-3" />
                Letzter YAML-Import: {formatGermanDateTime(stats.last_import)}
              </span>
            )}
          </p>
        </div>
        <Button
          data-testid="import-btn"
          onClick={handleImport}
          disabled={importing}
          className="h-11 px-6 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg shadow-[0_0_15px_rgba(239,105,76,0.3)] hover:shadow-[0_0_25px_rgba(239,105,76,0.5)] transition-all duration-200 active:scale-95"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${importing ? 'animate-spin' : ''}`} />
          {importing ? 'Importing...' : 'Import YAML'}
        </Button>
        <Button
          data-testid="sync-btn"
          onClick={handleCloudSync}
          disabled={syncing}
          className="h-11 px-6 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-lg shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] transition-all duration-200 active:scale-95"
        >
          <Cloud className={`w-4 h-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
          {syncing ? 'Syncing...' : 'Cloud Sync'}
        </Button>
      </div>

      {/* KPI Cards */}
      {stats && <KPICards stats={stats} />}

      <MailTroubleshooterWidget />

      <ConfigHistoryWidget />

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3" data-testid="filter-bar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
          <Input
            data-testid="search-input"
            placeholder="Search servers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-11 pl-10 bg-[#121214] border-white/10 focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C] text-white placeholder:text-[#52525b] rounded-lg"
          />
        </div>
        <Select value={filterProvider} onValueChange={setFilterProvider}>
          <SelectTrigger data-testid="filter-provider" className="w-full md:w-44 h-11 bg-[#121214] border-white/10 text-white">
            <SelectValue placeholder="Provider" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-white/10">
            <SelectItem value="all">All Providers</SelectItem>
            {providers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger data-testid="filter-role" className="w-full md:w-44 h-11 bg-[#121214] border-white/10 text-white">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-white/10">
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterHealth} onValueChange={setFilterHealth}>
          <SelectTrigger data-testid="filter-health" className="w-full md:w-48 h-11 bg-[#121214] border-white/10 text-white">
            <SelectValue placeholder="Health" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-white/10">
            <SelectItem value="all">All Health</SelectItem>
            <SelectItem value="healthy">Healthy</SelectItem>
            <SelectItem value="critical">Has Issues</SelectItem>
            <SelectItem value="cert_expired">Cert Expired</SelectItem>
            <SelectItem value="cert_expiring">Cert Expiring</SelectItem>
            <SelectItem value="failed_units">Failed Units</SelectItem>
            <SelectItem value="high_memory">High Memory</SelectItem>
            <SelectItem value="high_disk">High Disk</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Server Table */}
      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa] font-semibold uppercase text-xs tracking-wider">Server</TableHead>
                <TableHead className="text-[#a1a1aa] font-semibold uppercase text-xs tracking-wider">Provider</TableHead>
                <TableHead className="text-[#a1a1aa] font-semibold uppercase text-xs tracking-wider hidden lg:table-cell">Host / IP</TableHead>
                <TableHead className="text-[#a1a1aa] font-semibold uppercase text-xs tracking-wider hidden md:table-cell">FQDN</TableHead>
                <TableHead className="text-[#a1a1aa] font-semibold uppercase text-xs tracking-wider hidden xl:table-cell">Roles</TableHead>
                <TableHead className="text-[#a1a1aa] font-semibold uppercase text-xs tracking-wider">Health</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((server, idx) => (
                <TableRow
                  key={server.asset_id}
                  data-testid={`server-row-${server.asset_id}`}
                  onClick={() => navigate(`/servers/${server.asset_id}`)}
                  className="border-white/5 cursor-pointer hover:bg-white/[0.03] transition-colors duration-200 group"
                  style={{ animationDelay: `${idx * 40}ms` }}
                >
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        (server.health?.issues_count || 0) > 0 ? 'bg-[#F43F5E]/10' : 'bg-[#10B981]/10'
                      }`}>
                        <Server className={`w-4 h-4 ${
                          (server.health?.issues_count || 0) > 0 ? 'text-[#F43F5E]' : 'text-[#10B981]'
                        }`} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{server.meta?.alias || server.asset_id}</p>
                        <p className="text-[#52525b] text-xs font-mono">{server.asset_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-white/5 text-[#a1a1aa] border-white/10 text-xs font-mono">
                      {server.provider}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-sm font-mono text-[#a1a1aa]">{server.host}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-sm text-[#a1a1aa]">{server.primary_fqdn}</span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {(server.roles_guess || []).slice(0, 3).map(r => (
                        <Badge key={r} className="bg-[#EF694C]/10 text-[#EF694C] border-[#EF694C]/20 text-[10px]">
                          {r}
                        </Badge>
                      ))}
                      {(server.roles_guess || []).length > 3 && (
                        <Badge className="bg-white/5 text-[#52525b] border-white/10 text-[10px]">
                          +{server.roles_guess.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <HealthBadges health={server.health} compact />
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-[#52525b] group-hover:text-[#EF694C] transition-colors" />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-[#52525b]">
                    No servers found matching filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const KPICards = ({ stats }) => {
  const cards = [
    {
      label: 'Total Servers',
      value: stats.total_servers,
      icon: Server,
      color: '#EF694C',
      bgColor: '#EF694C',
    },
    {
      label: 'Healthy',
      value: stats.healthy_servers,
      icon: CheckCircle2,
      color: '#10B981',
      bgColor: '#10B981',
    },
    {
      label: 'Cert Issues',
      value: stats.cert_expired + stats.cert_expiring_soon,
      icon: ShieldAlert,
      color: stats.cert_expired > 0 ? '#F43F5E' : '#F59E0B',
      bgColor: stats.cert_expired > 0 ? '#F43F5E' : '#F59E0B',
      sub: stats.cert_expired > 0 ? `${stats.cert_expired} expired` : stats.cert_expiring_soon > 0 ? `${stats.cert_expiring_soon} expiring` : 'All valid',
    },
    {
      label: 'Failed Units',
      value: stats.failed_units,
      icon: XCircle,
      color: stats.failed_units > 0 ? '#F43F5E' : '#10B981',
      bgColor: stats.failed_units > 0 ? '#F43F5E' : '#10B981',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children" data-testid="kpi-cards">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.label}
            data-testid={`kpi-${card.label.toLowerCase().replace(/\s/g, '-')}`}
            className="bg-[#121214] border-white/10 rounded-xl hover:border-white/20 transition-colors duration-300 overflow-hidden"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-[#a1a1aa] uppercase tracking-wider mb-2">
                    {card.label}
                  </p>
                  <p className="text-4xl font-bold font-heading text-white">{card.value}</p>
                  {card.sub && (
                    <p className="text-xs mt-1" style={{ color: card.color }}>{card.sub}</p>
                  )}
                </div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${card.bgColor}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color: card.color }} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
